import { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  selectLoading,
  selectSelectedStore,
  selectStores,
} from "../../../features/Database/redux/selectors";
import { setSelectedStore } from "../../../features/Database/redux/slice";
import { getServicePagePath } from "../../../services/navigation/navigateToRoute";
import { useAppDispatch } from "../../../services/redux/hooks/use-dispatch";
import { useAppSelector } from "../../../services/redux/hooks/use-selector";
import {
  createLocalStoreAction,
  getLocalStoreFilesAction,
} from "../../../features/Database/redux/actions/local_store_actions";
import {
  createStoreAction,
  getStoreFilesAction,
  getStoresAction,
} from "../../../features/Database/redux/actions/openai_store_actions";
import {
  updateLocalStore,
  updateStore,
} from "../../../services/api/instances/vectorStoreApi/methods";
import { MenuItems } from "../../../common/AppSidebar/types";
import { APP_VECTOR_STORE_MODE } from "../../../services/api/constants";

export const useDatabasePage = () => {
  const dispatch = useAppDispatch();
  const stores = useAppSelector(selectStores);
  const selectedStore = useAppSelector(selectSelectedStore);
  const isStoreLoading = useAppSelector(selectLoading);
  const isLoading = isStoreLoading;
  const navigate = useNavigate();

  const storeSidebarItems: MenuItems = useMemo(
    () =>
      stores.map((store) => ({
        id: store.id,
        name: store.name,
        parentId: null,
        children: [],
        metadata: {
          itemType:
            store.metadata.type === "local" ? "local_base" : "openai_base",
        },
      })),
    [stores],
  );

  useEffect(() => {
    dispatch(getStoresAction(APP_VECTOR_STORE_MODE))
      .unwrap()
      .then((response) => {
        if (response.finalStores.length === 0) {
          navigate(getServicePagePath(null, "knowledge_base"));
          return;
        } else {
          const activeStore = response.finalStores[0];
          if (activeStore?.metadata && activeStore.metadata.type === "local") {
            dispatch(getLocalStoreFilesAction(activeStore?.id));
          } else {
            dispatch(getStoreFilesAction(activeStore?.id));
          }
          navigate(getServicePagePath(activeStore?.id, "knowledge_base"));
        }
      })
      .catch((error) => {
        if (error.name === "ConditionError") {
          if (stores.length > 0) {
            if (
              selectedStore?.metadata &&
              selectedStore.metadata.type === "local"
            ) {
              dispatch(getLocalStoreFilesAction(selectedStore?.id as string));
            } else {
              dispatch(getStoreFilesAction(selectedStore?.id as string));
            }
            navigate(
              getServicePagePath(selectedStore?.id as string, "knowledge_base"),
            );
          } else {
            navigate(getServicePagePath(null, "knowledge_base"));
          }
          return;
        }
        console.error("Unexpected error:", error);
        throw new Error(error);
      });
  }, []);

  const handleCreateStore = (mode: string) => {
    if (mode === "local" || mode === "both") {
      dispatch(createLocalStoreAction(""))
        .unwrap()
        .then((response) => {
          navigate(getServicePagePath(response.id, "knowledge_base"));
        });
      return;
    } else if (mode === "openai" || mode === "both") {
      dispatch(createStoreAction(""))
        .unwrap()
        .then((response) => {
          navigate(getServicePagePath(response.id, "knowledge_base"));
        });
      return;
    }
  };

  const handleSelectDatabase = useCallback(
    (id: string) => {
      const store = stores.find((item) => item.id === id);
      if (!store) return;
      dispatch(setSelectedStore(store));
      if (store?.files === undefined || store.files.length === 0) {
        if (store?.metadata && store.metadata.type === "local") {
          dispatch(getLocalStoreFilesAction(id));
          if (store.metadata.internal_id === "") {
            updateLocalStore(store.id, {
              name: store.name,
              metadata: { type: "local", internal_id: store.id },
            });
          }
        } else {
          dispatch(getStoreFilesAction(id));
          if (store.metadata.internal_id === "") {
            updateStore(store.id, {
              name: store.name,
              metadata: { type: "openai", internal_id: store.id },
            });
          }
        }
      }
      navigate(getServicePagePath(id, "knowledge_base"));
    },
    [stores],
  );

  return {
    storeSidebarItems,
    isLoading,
    selectedStore,
    handleCreateStore,
    handleSelectDatabase,
  };
};
